import type { AudienceStatus } from '@/lib/constants';
import type { AudienceStatusBadgeStatus } from '../audience-status-badge';

/**
 * Statuses an internal user can transition TO (snake_case wire values) — the
 * selectable subset of `AudienceStatus` (`draft` is pre-submission, not managed
 * from an audience surface).
 */
export type StatusPillValue = Exclude<AudienceStatus, 'draft'>;

export interface StatusMeta {
  value: StatusPillValue;
  label: string;
  badge: AudienceStatusBadgeStatus;
}

/**
 * Single source of truth for status → label + badge (NPJB-409), in lifecycle
 * order. Consumers — the dropdown, the static badge on Audience Overview, the
 * DZ-pill success toast — read from here instead of re-declaring status maps.
 * `draft` is intentionally omitted: a pre-submission wizard state, not a status
 * managed from an audience surface (matches the prototype).
 */
export const SELECTABLE_STATUSES: readonly StatusMeta[] = [
  { value: 'building_audience', label: 'Building Audience', badge: 'BUILDING_AUDIENCE' },
  { value: 'avails_ready', label: 'Avails Ready', badge: 'AVAILS_READY' },
  { value: 'activating', label: 'Activating', badge: 'ACTIVATING' },
  { value: 'campaign_active', label: 'Active', badge: 'CAMPAIGN_ACTIVE' },
  { value: 'paused', label: 'Paused', badge: 'PAUSED' },
  { value: 'delivered', label: 'Delivered', badge: 'DELIVERED' },
  { value: 'archived', label: 'Archived', badge: 'ARCHIVED' },
];

const META_BY_STATUS = new Map(SELECTABLE_STATUSES.map((s) => [s.value, s]));

// Lifecycle rank for promotion/demotion classification (NPJB-409 AC4). Index in
// `SELECTABLE_STATUSES` order; unlisted statuses (e.g. the pre-submission
// `draft`) rank -1, below every managed status, so leaving draft is never a
// demotion.
const STATUS_ORDER: readonly StatusPillValue[] = SELECTABLE_STATUSES.map((s) => s.value);

/** Lifecycle rank of a status (−1 for unlisted/`draft`). Lower = earlier. */
export const statusRank = (status: string): number =>
  STATUS_ORDER.indexOf(status as StatusPillValue);

/** Status → label + badge metadata, or undefined for unlisted statuses (e.g. draft). */
export const getStatusPillMeta = (status: string): StatusMeta | undefined =>
  META_BY_STATUS.get(status as StatusPillValue);

/** Human label for a status wire value (falls back to the raw value). */
export const statusPillLabel = (status: string): string =>
  META_BY_STATUS.get(status as StatusPillValue)?.label ?? status;
